// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what can be bought online through Paddle, and how a purchase maps
 * to Paddle prices and to the licence (`docs/AYNI_XR_LICENSING.md` "Online
 * payment"). Shared by the browser (`billing.ts`, `xrPlansUI.ts`) and the
 * site's server (`functions/api/billing/`), so both read a choice the same
 * way. Shapes only: no DOM, no network.
 *
 * Owner, 2026-10-07: online checkout for every plan sold today — Plus, the
 * Education plans Teacher and School, and the Research plans Researcher and
 * Lab. Plans by quote (District, Department) and the other segments stay by
 * invoice. Plus is monthly or yearly; the rest yearly only (owner,
 * 2026-10-08), the one price the plan table has (`xrPlans.ts`).
 *
 * Every Paddle price carries custom data `{ plan, item, interval, use? }`
 * (`item`: `plan`; `use`: `commercial` on the commercial Researcher
 * price), which the webhook reads; a price without it is not ours. Since
 * 2026-10-17 (owner, the pricing review) no packages are sold: Plus has
 * Live Earth and the highlights, every other plan every stream.
 */

import { XR_PLANS, type PlanUse, type XrPlanId } from './xrPlans'

export const ONLINE_PLANS = ['plus', 'teacher', 'school', 'researcher', 'lab'] as const
export type OnlinePlanId = (typeof ONLINE_PLANS)[number]
export type BillingInterval = 'month' | 'year'

export interface PlanChoice {
  plan: OnlinePlanId
  interval: BillingInterval
  /** Researcher (owner, 2026-10-17): academic or commercial use, the same licence at two prices. */
  use: PlanUse
}

export function isOnlinePlan(id: string): id is OnlinePlanId {
  return (ONLINE_PLANS as readonly string[]).includes(id)
}

/** The billing periods a plan is sold with online. */
export function intervalsFor(plan: OnlinePlanId): BillingInterval[] {
  return plan === 'plus' ? ['year', 'month'] : ['year']
}

/** Whether a plan is sold at an academic and a commercial price (Researcher). */
export function hasCommercialPrice(plan: OnlinePlanId): boolean {
  return XR_PLANS[plan as XrPlanId].commercialYearly !== undefined
}

/**
 * A choice as it is sold: every plan includes its streams (no packages are
 * picked since 2026-10-17), the plans but Plus are yearly, and only a plan
 * with a commercial price is told apart by use.
 */
export function normaliseChoice(plan: OnlinePlanId, interval: BillingInterval, use: PlanUse = 'academic'): PlanChoice {
  return {
    plan,
    interval: intervalsFor(plan).includes(interval) ? interval : 'year',
    use: hasCommercialPrice(plan) ? use : 'academic',
  }
}

/** A buyer's choice from a request body, normalised, or null when it is not one sold online. */
export function parseChoice(body: unknown): PlanChoice | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  if (typeof b.plan !== 'string' || !isOnlinePlan(b.plan)) return null
  const interval = b.interval === 'month' || b.interval === 'year' ? b.interval : null
  if (!interval || !intervalsFor(b.plan).includes(interval)) return null
  if (b.use !== undefined && b.use !== 'academic' && b.use !== 'commercial') return null
  return normaliseChoice(b.plan, interval, b.use === 'commercial' ? 'commercial' : 'academic')
}

/** One line of a checkout: the key of a Paddle price in the build's price map, and how many. */
export interface CheckoutLine {
  key: string
  quantity: number
}

/** The Paddle price for a plan itself: `plus/year`, `researcher/year`, `researcher-commercial/year`. */
export function planPriceKey(plan: OnlinePlanId, interval: BillingInterval, use: PlanUse = 'academic'): string {
  return use === 'commercial' && hasCommercialPrice(plan) ? `${plan}-commercial/${interval}` : `${plan}/${interval}`
}

/** The Paddle prices and quantities for a choice: one line, the plan. */
export function checkoutLines(choice: PlanChoice): CheckoutLine[] {
  return [{ key: planPriceKey(choice.plan, choice.interval, choice.use), quantity: 1 }]
}

/** Every price key a plan needs to be sold online. */
export function priceKeysFor(plan: OnlinePlanId): string[] {
  const keys = intervalsFor(plan).map((i) => planPriceKey(plan, i))
  return hasCommercialPrice(plan) ? [...keys, planPriceKey(plan, 'year', 'commercial')] : keys
}

/**
 * The package features a licence for this plan carries: `*` for a plan with
 * every stream, none for Plus, whose streams follow from the plan (Live
 * Earth and the highlights, `xrPlans.ts`).
 */
export function licencePackages(plan: OnlinePlanId): string[] {
  const access = XR_PLANS[plan as XrPlanId].packages
  return access === 'all' || access === 'all-hazards' ? ['*'] : []
}

/** People a plan's licence covers (its seats). */
export function seatsOf(plan: OnlinePlanId): number {
  return XR_PLANS[plan as XrPlanId].seats ?? 1
}
